import React, { useMemo } from 'react'
import { useRef } from 'react'

import useLocationHook from './util/useLocationHook'
import createMatcher from './util/createMatcher'
import createHistory from './util/createHistory'
import { PathnameContext, RouterContext, SearchContext, SearchParamsContext, LocationStateContext } from './util/contexts'

import type { RouterProps, RouterData, SearchParamsResult, SearchParamContextValue, SetSearchParamsFn } from './types'


const buildRouter = (opts: RouterProps = {}): RouterData => ({
  locationHook: opts.locationHook || useLocationHook,
  history: opts.history || createHistory(),
  matcher: createMatcher(),
})

const Router: React.FunctionComponent<RouterProps> = (props) => {
  const routerRef = useRef<RouterData>(null)
  const pathnameRef = useRef<string>(null)
  const searchParamsRef = useRef<SearchParamsResult>(null)

  if (!routerRef.current) {
    routerRef.current = buildRouter(props)
  }

  const locationState = routerRef.current.locationHook()
  const { pathname, search, searchParams } = locationState

  pathnameRef.current = pathname
  searchParamsRef.current = searchParams

  const setSearchParamsRef = useRef<SetSearchParamsFn>(null)

  if (!setSearchParamsRef.current) {
    setSearchParamsRef.current = (input, historyReplace = false, scroll = true) => {
      const newSearchParams = typeof input === 'function' ? input(searchParamsRef.current) : input
      routerRef.current.history[historyReplace ? 'replace' : 'push'](pathnameRef.current, {
        searchParams: newSearchParams,
        scroll,
      })
    }
  }

  // to fix useSearchParams rerender on pathname change
  const searchParamsState = useMemo<SearchParamContextValue>(() => [ searchParams, setSearchParamsRef.current ], [ searchParams ])

  return (
    <RouterContext.Provider value={routerRef.current}>
      <LocationStateContext.Provider value={locationState}>
        <PathnameContext.Provider value={pathname}>
          <SearchContext.Provider value={search}>
            <SearchParamsContext.Provider value={searchParamsState}>
              {props.children}
            </SearchParamsContext.Provider>
          </SearchContext.Provider>
        </PathnameContext.Provider>
      </LocationStateContext.Provider>
    </RouterContext.Provider>
  )
}


export default Router
